import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#quickAddGallery = this.closest('.quick-add-modal__content .product-information__media');
    if (this.#quickAddGallery) {
      this.#quickAddGallery.addEventListener('pointerdown', this.#handleQuickAddDragStart, { signal });
      this.#quickAddGallery.addEventListener('pointermove', this.#handleQuickAddDragMove, { signal });
      this.#quickAddGallery.addEventListener('pointerup', this.#handleQuickAddDragEnd, { signal });
      this.#quickAddGallery.addEventListener('pointercancel', this.#handleQuickAddDragEnd, { signal });
      this.#quickAddGallery.addEventListener('lostpointercapture', this.#handleQuickAddDragEnd, { signal });
    }
  }

  #controller = new AbortController();
  #quickAddGallery;
  #quickAddDragFrame;
  #quickAddDragState;
  #quickAddDragTargetScrollTop;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    cancelAnimationFrame(this.#quickAddDragFrame);
  }

  /** Starts mouse-drag scrolling for the vertical desktop quick-add gallery. */
  #handleQuickAddDragStart = (event) => {
    if (!this.#quickAddGallery || event.button !== 0 || event.pointerType === 'touch') return;
    if (!window.matchMedia('(min-width: 750px) and (pointer: fine)').matches) return;
    if (this.#quickAddGallery.scrollHeight <= this.#quickAddGallery.clientHeight) return;

    this.#quickAddDragState = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startScrollTop: this.#quickAddGallery.scrollTop,
    };
    this.#quickAddDragTargetScrollTop = this.#quickAddGallery.scrollTop;
    this.#quickAddGallery.setAttribute('gallery-dragging', '');
    this.#quickAddGallery.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  /** Updates drag scrolling once per animation frame to avoid pointer-event jitter. */
  #handleQuickAddDragMove = (event) => {
    if (!this.#quickAddGallery || this.#quickAddDragState?.pointerId !== event.pointerId) return;

    this.#quickAddDragTargetScrollTop =
      this.#quickAddDragState.startScrollTop - (event.clientY - this.#quickAddDragState.startY);

    cancelAnimationFrame(this.#quickAddDragFrame);
    this.#quickAddDragFrame = requestAnimationFrame(this.#applyQuickAddDragPosition);
    event.preventDefault();
  };

  #applyQuickAddDragPosition = () => {
    if (!this.#quickAddGallery || this.#quickAddDragState === undefined) return;
    this.#quickAddGallery.scrollTop = this.#quickAddDragTargetScrollTop;
  };

  #handleQuickAddDragEnd = (event) => {
    if (!this.#quickAddGallery || this.#quickAddDragState?.pointerId !== event.pointerId) return;

    cancelAnimationFrame(this.#quickAddDragFrame);
    this.#applyQuickAddDragPosition();

    if (this.#quickAddGallery.hasPointerCapture(event.pointerId)) {
      this.#quickAddGallery.releasePointerCapture(event.pointerId);
    }

    this.#quickAddGallery.removeAttribute('gallery-dragging');
    this.#quickAddDragState = undefined;
  };

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /** Explicit photo controls may navigate while zoomed; dragging still pans. */
  resetPhotoZoom() {
    this.querySelector('product-photo-zoom')?.controller?.reset();
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
